/**
 * RightSideModal — Modale latérale droite avec overlay
 * Pattern standard pour toutes les modales de création/édition
 */
import { ReactNode } from 'react';
import { NJHeading, NJIcon } from '@engie-group/fluid-design-system-react';

interface RightSideModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    icon?: string;
    children: ReactNode;
    footer?: ReactNode;
    metadata?: ReactNode;
}

export function RightSideModal({ isOpen, onClose, title, icon, children, footer, metadata }: RightSideModalProps) {
    if (!isOpen) return null;

    return (
        <>
            {/* Overlay */}
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 99998,
                }}
                onClick={onClose}
            />
            
            {/* Modale latérale droite */}
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    height: '100vh',
                    maxHeight: '100vh',
                    width: '600px',
                    backgroundColor: 'white',
                    boxShadow: '-4px 0 12px rgba(0,0,0,0.15)',
                    zIndex: 99999,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                {/* Header */}
                <div
                    style={{
                        padding: '12px 24px',
                        borderBottom: '1px solid var(--nj-core-color-grey-200)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexShrink: 0,
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {icon && (
                            <NJIcon 
                                name={icon} 
                                style={{ fontSize: 24, color: 'var(--nj-semantic-color-text-brand-primary-default)' }} 
                            />
                        )}
                        <NJHeading as="h3" scale="md">
                            {title}
                        </NJHeading>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            fontSize: '20px',
                            lineHeight: 1,
                            padding: '0',
                            width: '24px',
                            height: '24px',
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* Body scrollable */}
                <div style={{ flex: '1 1 0', overflowY: 'auto', padding: '16px 24px', minHeight: 0 }}>
                    {children}
                </div>

                {/* Métadonnées (optionnel) */}
                {metadata && (
                    <div
                        style={{
                            padding: '6px 24px',
                            backgroundColor: 'var(--nj-semantic-color-surface-neutral-subtle)',
                            borderTop: '1px solid var(--nj-core-color-grey-200)',
                            fontSize: '10px',
                            lineHeight: '1.2',
                            color: 'var(--nj-semantic-color-text-neutral-secondary-default)',
                            flexShrink: 0,
                        }}
                    >
                        {metadata}
                    </div>
                )}

                {/* Footer */}
                {footer && (
                    <div
                        style={{
                            padding: '10px 24px',
                            borderTop: '1px solid var(--nj-core-color-grey-200)',
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'flex-end',
                            backgroundColor: 'white',
                            flexShrink: 0,
                        }}
                    >
                        {footer}
                    </div>
                )}
            </div>
        </>
    );
}
